<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <hero-bar>
      Suppliers List
      <div slot="right">
        <b-button
          v-if="isEditAllowed"
          type="is-primary import-button"
          @click="clickAddSupplier"
          >+ &nbsp;Add Supplier</b-button
        >
      </div>
    </hero-bar>
    <section class="section is-main-section">
      <circul8-table
        :records="data"
        :fields="columns"
        :usePropRecords="true"
        :listingComponent="true"
        :isCheckable="false"
        :maxTextLength="'48'"
        :isLoading="isDataLoading"
        :showEditButton="true"
        :backendSortingEnabled="true"
        :routeNext="false"
        :showRemoveButton="isEditAllowed"
        @clickEdit="clickEditSupplier"
        @clickView="viewSupplier"
        @clickDelete="deleteModal"
        @backendSort="sortData"
        @querySet="searchByFilter"
        @loadNextPage="pullNextPage"
        :enableInfiniteScroll="true"
        :hasNextData="nextData"
        :hasPreviousData="previousData"
        @loadPreviousPage="pullPreviousPage"
        @actionIconClick="handleActionIconClick"
      />
      <div class="records-displayed" v-if="totalRecords > 0">
        <span v-if="currentPage * 50 + 50 < totalRecords"
          >displaying {{ currentPage > 1 ? currentPage * 50 - 50 : 0 }} -
          {{
            currentPage > 0
              ? nextPageCursor
                ? currentPage * 50 - 50 + data.length
                : totalRecords
              : data.length
          }}
          records out of {{ totalRecords }} records</span
        >
        <div v-else>
          <span v-if="currentPage == 0">
            displaying 0 - {{ totalRecords }} records out of
            {{ totalRecords }} records
          </span>
          <span v-else>
            displaying {{ currentPage * 50 - 50 }} - {{ totalRecords }} records
            out of {{ totalRecords }} records
          </span>
        </div>
      </div>
    </section>
    <supplier-form
      :is-active="isModalActive"
      :view-only="viewOnly"
      :action="action"
      :editObjectName="editObjectName"
      @confirm="addSupplier"
      @cancel="editCancel"
      @editSupplier="editSupplier"
    />
  </div>
</template>

<script>
import TitleBar from "@/components/TitleBar";
import HeroBar from "@/components/HeroBar";
import Circul8Table from "../../components/Circul8Table.vue";
import SuppliersService from "../../services/SuppliersService";
import SupplierForm from "@/components/Suppliers/SupplierForm.vue";
import { mapGetters } from "vuex";
import UtilityMixin from "@/mixins/UtilityMixin";
import ScopeMixin from "@/mixins/scopeMixin";
import { UserGroup } from "@/services/UserGroups";
import * as dayjs from "dayjs";

export default {
  name: "SuppliersList",
  mixins: [UtilityMixin, ScopeMixin],
  components: {
    HeroBar,
    TitleBar,
    Circul8Table,
    SupplierForm
  },
  data() {
    return {
      data: [],
      formData: {},
      isDataLoading: false,
      isModalActive: false,
      action: null,
      isAdd: false,
      editObject: {
        name: "",
        updated_by_name: "",
        updated_at: ""
        // add other fields as needed for a blank supplier
      },
      viewOnly: false,
      nextPageCursor: "",
      PreviousPageCursor: "",
      loadMoreClicked: false,
      lastSortColumn: null,
      lastSortOrder: null,
      enableLoadMore: false,
      totalRecords: 0,
      isLoading: false,
      nextData: false,
      previousData: false,
      currentPage: 0,
      query: "",
      columns: [
        {
          field: "code",
          label: "Supplier ID",
          searchable: true,
          filterKey: { code: "" },
          type: "input",
          actionIcon: "mdi mdi-open-in-new",
          actionName: "viewProducts"
        },
        {
          field: "name",
          label: "Supplier Name",
          searchable: true,
          filterKey: { name: "" },
          type: "input"
        },
        {
          field: "updated_by_name",
          label: "Last Updated By",
          searchable: true,
          filterKey: { updated_by_name: "" },
          type: "input"
        },
        {
          field: "updated_at",
          label: "Last Updated",
          searchable: true,
          filterKey: { updated_at: "" },
          type: "dateField"
        },
        {
          field: "created_at",
          label: "Created At",
          searchable: true,
          filterKey: { created_at: "" },
          type: "dateField"
        },
        {
          field: "actions",
          label: "",
          headerClass: "is-actions",
          cellClass: "is-actions",
          visible: true,
          searchable: true,
          type: "actionSearch",
          actionSearch: true,
          filterKey: { actions: "" }
        }
      ],
      SuppliersService: new SuppliersService()
    };
  },
  mounted() {
    this.checkExternalPermission();
    this.fetchSuppliersList();
  },
  computed: {
    ...mapGetters(["userId", "userName", "userHasPermission", "userGroups"]),
    editObjectName() {
      return this.editObject || this.getClearFormObject();
    },
    titleStack() {
      return [{ url: "#", name: "Suppliers" }];
    },
    isEditAllowed() {
      return !this.userGroups.includes(UserGroup.VIEW_ONLY);
    }
  },
  methods: {
    clickAddSupplier() {
      this.viewOnly = false;
      this.action = "Add";
      this.isAdd = true;
      this.editObject = this.getClearFormObject();
      this.isModalActive = true;
    },
    editFromView(editObject) {
      this.action = "Edit";
      this.isAdd = true;
      this.editObject = editObject;
      this.isModalActive = true;
    },
    getClearFormObject() {
      return {
        name: "",
        updated_by_name: "",
        updated_at: ""
        // add other fields as needed for a blank supplier
      };
    },
    editSupplier() {
      this.isModalActive = false;
    },
    clickEditSupplier(editObject) {
      this.action = "Edit";
      this.isAdd = true;
      this.editObject = editObject;
      this.isModalActive = true;
    },
    viewSupplier(editObject) {
      this.editObject = editObject;
      this.isModalActive = true;
      this.viewOnly = true;
      this.action = "View";
    },

    addSupplier(formData) {
      this.isModalActive = false;
      this.isDataLoading = true;

      const serviceCall =
        this.action === "Edit"
          ? this.SuppliersService.editSupplier(formData)
          : this.SuppliersService.addSupplier(formData);

      const actionMessage = this.action === "Edit" ? "updated" : "added";

      serviceCall.subscribe(
        () => {
          this.$buefy.toast.open({
            message: `Supplier ${actionMessage} successfully`,
            type: "is-success"
          });
          this.data = [];
          this.currentPage = 0;
          this.nextPageCursor = null;
          this.query = "";
          this.fetchSuppliersList();
        },
        e => {
          this.handleApiError(e);
          this.isDataLoading = false;
        }
      );
    },

    editCancel() {
      this.isModalActive = false;
    },
    handleActionIconClick(row, col) {
      if (col.actionName === "viewProducts") {
        this.navigateToProductsWithSupplierFilter(row);
      }
    },
    navigateToProductsWithSupplierFilter(supplier) {
      // Navigate to Products page with Public customer (ID = 0) and supplier filter
      // The supplier_number and supplier_name filters will be applied to the Product Sheet
      this.$router.push({
        name: "datamanagement.product",
        params: { customerId: 0 },
        query: {
          supplier_number: supplier.code,
          supplier_name: supplier.name
        }
      });
    },
    prepareData(data) {
      const dataList = [];
      for (const report in data) {
        const reportData = data[report];
        reportData["updated_at"] = dayjs(reportData["updated_at"]).format(
          "YYYY-MM-DD, HH:mm"
        );
        reportData["created_at"] = dayjs(reportData["created_at"]).format(
          "YYYY-MM-DD, HH:mm"
        );
        reportData["actions"] = this.isEditAllowed;
        reportData["delete"] = this.isEditAllowed;
        dataList.push(reportData);
      }

      if (this.nextPageCursor == null) {
        this.data.push(...dataList);
        if (this.currentPage > 1) {
          this.currentPage = this.currentPage - 1;
          if (this.currentPage == 1) {
            this.previousData = false;
          }
        } else {
          this.currentPage = this.currentPage - 1;
          this.previousData = false;
        }
      } else {
        this.data = dataList;
      }
      this.isDataLoading = false;
    },
    pullNextPage() {
      this.previousData = true;
      this.loadMoreClicked = true;
      this.currentPage = this.currentPage + 1;
      const temp = this.nextPageCursor.split("?")[1].split("&");
      const query_part = temp.filter(function(value) {
        return value.indexOf("page=") == -1;
      });
      if (this.nextPageCursor != null || this.nextPageCursor != undefined) {
        this.query =
          "&" + query_part.join("&") + "&page=" + (this.currentPage + 1) + "&";
        this.fetchSuppliersList(this.query, "next");
      }
    },
    pullPreviousPage() {
      this.nextData = true;
      this.loadMoreClicked = true;
      const temp = this.PreviousPageCursor.split("?")[1].split("&");
      let query_part = temp.filter(function(value) {
        return value.indexOf("page=") == -1;
      });
      query_part = [...new Set(query_part)];
      if (this.currentPage == 0 || this.currentPage == 1) {
        this.query = "&" + query_part.join("&") + "&page=1&";
        this.previousData = false;
      } else {
        this.query =
          "&" + query_part.join("&") + "&page=" + this.currentPage + "&";
      }
      this.currentPage =
        this.currentPage > 0 ? this.currentPage - 1 : this.currentPage;
      this.fetchSuppliersList(this.query, "prev");
    },
    searchByFilter(filterQuery) {
      try {
        this.isDataLoading = true;
        this.nextPageCursor = null;
        this.currentPage = 0;
        this.data = [];
        this.nextData = false;
        this.query = "&" + filterQuery;

        let query = "";
        if (this.lastSortColumn) {
          query += "&order_field" + "=" + this.lastSortColumn;
          query += "&order_by" + "=" + this.lastSortOrder;
          query += this.query;
        } else {
          query = this.query;
        }
        this.fetchSuppliersList(query);
      } catch (e) {
        console.error("Error in searchByFilter:", e);
        this.handleApiError(e);
        this.isDataLoading = false;
      }
    },
    sortData(field, order) {
      try {
        this.isDataLoading = true;
        this.loadMoreClicked = false;
        this.nextPageCursor = null;
        this.currentPage = 0;
        this.data = [];
        this.nextData = false;
        // sort should always fetch page 1, so removing page param if exist
        this.query = this.query.replace(/page=[0-9.]{1,2}&/g, "");
        const oldOrderField = "&order_field" + "=" + this.lastSortColumn;
        const oldOrderValue = "&order_by" + "=" + this.lastSortOrder;
        const newOrderField = "&order_field" + "=" + field;
        const newOrderValue = "&order_by" + "=" + order;

        this.query = this.replaceOrAddParanInSearchQuery(
          oldOrderField,
          newOrderField
        );
        this.query = this.replaceOrAddParanInSearchQuery(
          oldOrderValue,
          newOrderValue
        );

        this.lastSortColumn = field;
        this.lastSortOrder = order;
        this.fetchSuppliersList(this.query);
      } catch (e) {
        console.error("Error in sortData:", e);
        this.handleApiError(e);
        this.isDataLoading = false;
        this.data = [];
      }
    },

    replaceOrAddParanInSearchQuery(oldParam, newParam) {
      try {
        if (this.query && this.query.includes(oldParam)) {
          this.query = this.query.replace(oldParam, newParam);
        } else {
          this.query = (this.query || "") + newParam;
        }
        return this.query;
      } catch (e) {
        console.error("Error in replaceOrAddParanInSearchQuery:", e);
        return this.query || "";
      }
    },
    fetchSuppliersList(query = "", scrollDir) {
      this.isLoading = true;
      //this.data = [];
      this.SuppliersService.getSuppliersData(query).subscribe(
        response => {
          this.PreviousPageCursor = this.nextPageCursor
            ? this.nextPageCursor
            : this.PreviousPageCursor;
          this.nextPageCursor = response["next"];
          if (this.currentPage > 1) {
            this.previousData = true;
            if (this.nextPageCursor != null) {
              this.enableLoadMore = this.nextData = true;
            } else {
              this.enableLoadMore = this.nextData = false;
            }
          } else {
            this.enableLoadMore = this.previousData = false;
            if (this.nextPageCursor != null) {
              this.enableLoadMore = this.nextData = true;
            } else {
              this.enableLoadMore = this.nextData = false;
            }
          }
          this.totalRecords = response["count"];
          let tempData = [];
          if (scrollDir == "next") {
            //this.totalRecords = this.data.length + response["count"];
            if (!this.nextPageCursor) {
              this.prepareData(response["results"]);
            } else {
              tempData = [...this.data, ...response["results"]];
              if (tempData.length > 100 && this.nextPageCursor != null) {
                tempData = tempData.slice(50, 150);
              }
              this.prepareData(tempData);
            }
          } else if (scrollDir == "prev") {
            if (this.currentPage == 0) {
              this.prepareData(response["results"]);
            } else {
              tempData = [...response["results"], ...this.data];
              if (tempData.length >= 100) {
                tempData = tempData.slice(0, 100);
              }
              this.prepareData(tempData);
            }
          } else {
            this.prepareData(response["results"]);
          }
          this.isLoading = false;
        },
        e => {
          console.log(e);
          this.data = [];
          this.isLoading = false;
          this.isDataLoading = false;
          this.nextData = false;
        }
      );
    },
    handleApiError(reason) {
      let errorMessage = null;
      if (reason?.response?.data) {
        if (reason.response.data["detail"]) {
          errorMessage = reason.response.data["detail"];
        } else if (reason.response.data["error"]) {
          errorMessage = reason.response.data["error"];
        } else if (reason.response.data["name"]) {
          errorMessage = reason.response.data["name"];
        } else {
          errorMessage = "Invalid input";
        }
      }
      this.$buefy.dialog.alert({
        title: "Error",
        message: errorMessage,
        type: "is-danger",
        ariaRole: "alertdialog",
        ariaModal: true
      });
      this.data = [];
      this.currentPage = 0;
      this.nextPageCursor = null;
      this.fetchSuppliersList();
      this.isDataLoading = false;
      this.isLoading = false;
    },

    deleteModal(editObject) {
      this.$buefy.dialog.confirm({
        title: "Deleting Supplier",
        message: "Are you sure you want to <b>delete</b> this Supplier?",
        confirmText: "Delete supplier",
        hasIcon: true,
        onConfirm: () => this.deleteSupplier(editObject)
      });
    },
    deleteSupplier(editObject) {
      try {
        this.SuppliersService.deleteSupplier(editObject.id).subscribe(
          () => {
            this.$buefy.toast.open({
              message: "Supplier deleted successfully",
              type: "is-success"
            });
            this.data = [];
            this.currentPage = 0;
            this.nextPageCursor = null;
            this.query = "";
            this.fetchSuppliersList();
          },
          e => {
            this.handleApiError(e);
            this.isDataLoading = false;
          }
        );
      } catch (e) {
        this.handleApiError(e);
        this.isDataLoading = false;
      }
    }
  }
};
</script>
